import { type CSSProperties, Fragment, type ReactNode, useEffect, useRef } from "react";
import { shellThemeTokens } from "./ds-shell";
import { C, FU, T, W } from "./tokens";

/* ────────────────────────────────────────────────────────────────────────────
   IntelShell — the Intelligence sections' shared dark "observatory" shell
   (design-system v2 §A8), extracted verbatim from the approved 1.3 Source
   markets conversion so 1.4+ are genuinely the same product, not lookalikes.

   Owns: the fixed layered parallax background (navy ramp + two glow planes +
   dot grid, driven by --src-scroll), the non-navigating breadcrumb, the
   .src-enter staggered entrance, and the frosted-card overrides for MetricMap
   surfaces (.mmap-card / .mmap-panel / .mmap-ends / .mmap-bar-fill).

   Class names keep the original .src-* prefix from the 1.3 extraction —
   renaming them would risk pixel drift for zero benefit; only one section
   mounts at a time so the scoping never collides.

   A local scoped block rather than <DsShell> because DsShell's one-screen CSS
   hides the collapsible right rail, which shell sections keep.
   ──────────────────────────────────────────────────────────────────────────── */

export function IntelShell({
  id,
  crumb,
  accent,
  hl,
  children,
}: {
  id: string;
  /** Breadcrumb segments, e.g. ["Intelligence", "Global overview", "Corridors"].
   *  Orientation only — non-navigating (§A8.6). Omit to suppress the breadcrumb
   *  entirely (sections that carry their own top command bar instead). */
  crumb?: string[];
  /** Workspace identity hue for the foreground parallax glow. Defaults to the
   *  Intelligence blue baked into the layer; Prioritization passes its purple. */
  accent?: string;
  /** Selection-row highlight, keyed to the workspace's DATA hue (C.hlAzure for
   *  Demand Signals, C.hlTeal for Intelligence). Overrides --color-highlight-soft
   *  for everything in the shell that reads C.goldLight (the ranked-list active
   *  row, visa subject row, etc.). Omit to inherit the DS-shell default gold —
   *  correct only for Prioritization, whose data hue IS gold. */
  hl?: string;
  children: ReactNode;
}) {
  // Scroll-reactive background: an rAF-throttled scroll listener writes the
  // scrolled distance into --src-scroll on the shell; the fixed glow layers
  // translate off it at different rates (parallax). The distance is measured
  // from the shell's own bounding rect (first-paint top − current top), NOT
  // window.scrollY — the shell can sit inside a nested scroll pane, where
  // scrollY never moves. The listener is capture-phase on document for the
  // same reason: scroll events don't bubble, so capture is the only way to
  // hear every scroller (window included). Host sections mount the shell only
  // once their data lands, so arming on mount measures the true first paint.
  // Skipped entirely under reduced motion.
  const shellRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = shellRef.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    let baseTop: number | null = null;
    const measure = () => {
      raf = 0;
      const top = el.getBoundingClientRect().top;
      if (baseTop === null) baseTop = top; // first paint = scroll origin
      el.style.setProperty("--src-scroll", String(Math.max(0, baseTop - top)));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener("scroll", onScroll, { capture: true });
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      id={id}
      className="src-shell"
      ref={shellRef}
      style={
        accent || hl
          ? ({
              ...(accent ? { "--src-accent": accent } : {}),
              ...(hl ? { "--color-highlight-soft": hl } : {}),
            } as CSSProperties)
          : undefined
      }
    >
      {/* Full-page layered background (fixed): landing navy ramp + two glow
          layers on separate parallax rates + the GTM dot-grid texture. */}
      <div className="src-bg" aria-hidden="true">
        <div className="src-bg-glow-a" />
        <div className="src-bg-glow-b" />
        <div className="src-bg-grid" />
      </div>

      {/* Non-navigating breadcrumb — orientation only, in the row the removed
          chrome left empty. Typography mirrors the app's BreadcrumbBar. Omitted
          when a section supplies its own top command bar. */}
      {crumb && crumb.length > 0 ? (
        <nav className="src-crumb src-enter" style={{ "--d": 0 } as CSSProperties} aria-label="Breadcrumb">
          {crumb.map((seg, i) => (
            <Fragment key={seg}>
              {i > 0 ? (
                <span className="src-crumb-sep" aria-hidden="true">
                  /
                </span>
              ) : null}
              <span className={i === crumb.length - 1 ? "src-crumb-seg src-crumb-current" : "src-crumb-seg"}>
                {seg}
              </span>
            </Fragment>
          ))}
        </nav>
      ) : null}

      {children}
      <style>{INTEL_SHELL_STYLES}</style>
    </section>
  );
}

/* Static stylesheet — built once at module load (same hoisting rationale as
   SectionShell's). All interpolations are static design tokens. */
const INTEL_SHELL_STYLES = `
  /* ── The Intelligence "observatory" shell (design-system v2 §A8): the paired
        shell token block over the landing page's ramp. shellThemeTokens emits
        the dark block on the bare .src-shell and the light block under
        [data-theme="light"] .src-shell, so the whole shell (background, cards,
        text, ocean) flips with the toggle. extraDark keeps Intelligence's
        deeper observatory ocean (#05070e); light inherits the pale ocean. ── */
  ${shellThemeTokens(".src-shell", "--color-map-ocean: #05070e;")}
  .src-shell {
    position: relative;
    color: var(--color-text-primary);
    /* The de-chromed shell has no sticky chrome, so the section supplies its
       own top breathing room (the breadcrumb row). Only one section mounts per
       page, so the space below is trailing, not inter-section rhythm — keep it
       to a light breather so short sections don't trip a phantom scroll
       (design §0.2 minimal-scroll). */
    padding-top: 20px;
    margin-bottom: 24px;
  }

  /* ── Background: the landing hero's navy ramp on a fixed full-viewport
        layer, with two colour glows that parallax at different rates off
        --src-scroll (written by the shell's scroll listener) and the GTM
        dot-grid texture, top-masked. z-index 0 keeps the stack under all
        section content (z-index 1 below) and the fixed sidebar (40). ── */
  .src-bg {
    position: fixed;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    overflow: hidden;
    background: radial-gradient(125% 90% at 50% -10%, #18223e 0%, #0c1124 42%, #080b16 100%);
  }
  /* Light: the observatory dome inverts to a soft top-lit pale ramp on the
     tinted canvas — same top-lit geometry, so the shell keeps its depth. */
  [data-theme="light"] .src-bg {
    background: radial-gradient(125% 90% at 50% -10%, #f0f3fa 0%, #e7eef7 46%, #dfe7f3 100%);
  }
  .src-bg-glow-a,
  .src-bg-glow-b {
    position: absolute;
    inset: -25% 0;
    will-change: transform;
  }
  .src-bg-glow-a {
    background: radial-gradient(circle at 82% 14%, color-mix(in srgb, var(--src-accent, #82BAFF) 14%, transparent), transparent 34%);
    transform: translateY(calc(var(--src-scroll, 0) * -0.22px));
  }
  .src-bg-glow-b {
    background: radial-gradient(circle at 12% 58%, rgba(38, 197, 173, 0.09), transparent 34%);
    transform: translateY(calc(var(--src-scroll, 0) * -0.1px));
  }
  .src-bg-grid {
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px);
    background-size: 28px 28px;
    mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.5), transparent 60%);
  }
  /* Light: the two hue glows stay (workspace accent + teal) but whisper-soft on
     white, and the dot grid inverts to navy-alpha. The mask is alpha-only, so
     it needs no light variant. */
  [data-theme="light"] .src-bg-glow-a {
    background: radial-gradient(circle at 82% 14%, color-mix(in srgb, var(--src-accent, #82BAFF) 12%, transparent), transparent 42%);
  }
  [data-theme="light"] .src-bg-glow-b {
    background: radial-gradient(circle at 12% 58%, rgba(38, 197, 173, 0.06), transparent 38%);
  }
  [data-theme="light"] .src-bg-grid {
    background-image: radial-gradient(rgba(0, 15, 71, 0.05) 1px, transparent 1px);
  }
  .src-shell > :not(.src-bg) { position: relative; z-index: 1; }

  /* ── Non-navigating breadcrumb (typography = the app's .bcrumb-trail). ── */
  .src-crumb {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: ${FU};
    font-size: ${T.cap}px;
    color: ${C.textMd};
    margin-bottom: 22px;
  }
  .src-crumb-seg { white-space: nowrap; }
  .src-crumb-sep { color: ${C.textLo}; }
  .src-crumb-current { color: ${C.textHi}; font-weight: ${W.medium}; }

  /* ── Map card: frosted raised surface (GTM 4.1 recipe) — semi-opaque white
        gradient over the dark ramp, soft drop shadow, backdrop blur. No
        accent stripes. The flair (navy hero) panel now carries its own surface
        via .mmap-panel-flair, so the transparent rule below EXCLUDES it (:not)
        — otherwise this shell rule, injected after MetricMap's <style>, would
        win on equal specificity and blank the hero gradient. ── */
  .src-shell .mmap-card {
    background: linear-gradient(180deg, var(--ds-card-a), var(--ds-card-b));
    border-radius: 12px;
    box-shadow: var(--shadow-card-rest);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }
  /* Light: the card is solid white (--ds-card-a/b), so the glass blur has
     nothing to lift off — drop it and let a hairline border + the navy-alpha
     shadow (both token-driven) carry the edge. */
  [data-theme="light"] .src-shell .mmap-card {
    border: 1px solid var(--color-border);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
  .src-shell .mmap-panel:not(.mmap-panel-flair) { background: transparent; }
  /* Match the GTM report grid's 18px gutter on the ranking pair. */
  .src-shell .mmap-ends { gap: 18px; }
  .src-two {
    display: grid;
    /* auto-fit + a real 440px floor (not minmax(0,…)) so the pair only sits
       side-by-side while each board is wide enough for its LONGEST country name
       ("United Arab Emirates" needs ~130px in the name cell; a 397px board clips
       it, a 440px+ board doesn't). Below that floor — desktop at ~125–150% zoom or
       a landscape tablet — the track can't hold two 440px columns, so the boards
       stack full-width instead of crushing the name column to an ellipsis (or, on
       the emerging/lagging pair, a single letter). A two-up column is therefore
       never in the 360–440px truncating zone. Container-driven, so it accounts
       for the sidebar; desktop at 100% (~1440) stays two-up. */
    grid-template-columns: repeat(auto-fit, minmax(440px, 1fr));
    gap: 18px;
  }
  /* Phone: the paired rank cards can't share a row — each needs ~277px and a
     phone column is ~166px, so side-by-side overflows the viewport (drags the
     whole page sideways, pushing the fixed chrome off). Stack them. Below 900px
     only MobileShell renders, so this never touches the desktop sidebar layout. */
  @media (max-width: 899px) {
    .src-two {
      grid-template-columns: 1fr;
    }
  }

  /* ── Nested-DsShell alignment (Prioritization 2.1 Process, 2.2 Model admin).
        Those two sections mount a <DsShell> INSIDE this shell to borrow its
        .ds-* primitives (chips/title). DsShell is built to be a direct child of
        the dashboard body as a one-screen section, so it carries a responsive
        horizontal padding (base + a max-height:860px clamp(18px,3vw,28px)
        variant) that here insets its content ~3vw from this shell's full-width
        header/lede — the tables never line up with the title above them, and
        every OTHER IntelShell section (which has no nested DsShell) sits flush.
        Cancel the horizontal inset + the one-screen right-margin pull so nested
        content aligns with the header at every width, matching the other pages.
        Vertical padding is left intact. ── */
  .src-shell .ds-shell {
    padding-left: 0;
    padding-right: 0;
    margin-right: 0;
  }

  /* ── Entrance choreography (motion only under no-preference):
        crumb → header → cards rise in sequence; the map legend wipes in
        left→right; ranking bars fill 0→width. ── */
  @media (prefers-reduced-motion: no-preference) {
    .src-enter {
      animation: srcEnter 520ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
      animation-delay: calc(var(--d, 0) * 90ms);
    }
    .src-shell .mmap-ends > *:first-child {
      animation: srcEnter 520ms cubic-bezier(0.22, 0.61, 0.36, 1) 270ms both;
    }
    .src-shell .mmap-ends > *:last-child {
      animation: srcEnter 520ms cubic-bezier(0.22, 0.61, 0.36, 1) 360ms both;
    }
    .src-shell .mmap-legend-bar {
      animation: srcLegend 700ms cubic-bezier(0.22, 0.61, 0.36, 1) 500ms both;
      transform-origin: left center;
    }
    .src-shell .mmap-bar-fill {
      animation: srcBarFill 800ms cubic-bezier(0.22, 0.61, 0.36, 1) 350ms both;
    }
  }
  @keyframes srcEnter {
    from { opacity: 0; transform: translateY(10px); }
  }
  @keyframes srcLegend {
    from { transform: scaleX(0); opacity: 0; }
  }
  @keyframes srcBarFill {
    from { width: 0; }
  }

  /* ── Hero trajectory — the navy flair panel's lower-half sparkline (rendered
        by HeroTrajectory through MetricMap.panelExtra; shared by 1.2 arrivals +
        1.3 spend). Base styles are the settled (fully drawn) state; the draw-in
        lives in the no-preference block below, so reduced-motion users see the
        finished chart immediately. ── */
  .src-traj {
    margin-top: 20px;
    padding-top: 14px;
    border-top: 1px solid ${C.border};
  }
  .src-traj-head {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 10px;
    font-family: ${FU};
  }
  .src-traj-cap {
    font-size: 10px;
    color: ${C.textLo};
    text-transform: uppercase;
    letter-spacing: 0.14em;
  }
  .src-traj-span { font-size: 10.5px; color: ${C.textMd}; }
  .src-traj-chip {
    margin-left: auto;
    font-size: 13px;
    font-weight: ${W.semibold};
    font-variant-numeric: tabular-nums;
  }
  /* Fixed height (not aspect-driven) so the sparkline can't grow taller as the
     panel widens and overflow the fixed-height card — that's the crop when the
     sidebar collapses. preserveAspectRatio="none" lets it fill the full width
     at this height; overflow:hidden is the hard "never spill" backstop. */
  .src-traj-svg { display: block; width: 100%; height: 88px; overflow: hidden; }

  /* Light theme — the sparkline's navy-tuned sky blues (inline #8fc0ff line,
     #dceaff dot, and the 130,186,255 fill stops) are too pale to read on the
     periwinkle hero panel; deepen them to the panel's kicker blue. Dark keeps
     the inline SVG values (no rule here), so it stays byte-identical. */
  [data-theme="light"] .src-traj-line { stroke: #2b5fb0; }
  [data-theme="light"] .src-traj-dot { fill: #2b5fb0; }
  [data-theme="light"] #src-traj-fill stop:first-child { stop-color: rgba(43, 95, 176, 0.2); }
  [data-theme="light"] #src-traj-fill stop:last-child { stop-color: rgba(43, 95, 176, 0); }

  @media (prefers-reduced-motion: no-preference) {
    /* Trajectory draw-in — replays per selection (the SVG is keyed by ISO):
       the line draws left→right (pathLength=1 normalises the dash), the
       gradient fill fades up behind it, the endpoint dot pops last. */
    .src-traj-line {
      stroke-dasharray: 1;
      stroke-dashoffset: 1;
      animation: srcTrajDraw 900ms cubic-bezier(0.22, 0.61, 0.36, 1) 120ms forwards;
    }
    .src-traj-area {
      opacity: 0;
      animation: srcTrajFade 650ms ease 480ms forwards;
    }
    .src-traj-dot {
      opacity: 0;
      transform: scale(0.4);
      transform-box: fill-box;
      transform-origin: center;
      animation: srcTrajDot 320ms cubic-bezier(0.34, 1.56, 0.64, 1) 900ms forwards;
    }
  }
  @keyframes srcTrajDraw {
    to { stroke-dashoffset: 0; }
  }
  @keyframes srcTrajFade {
    to { opacity: 1; }
  }
  @keyframes srcTrajDot {
    to { opacity: 1; transform: scale(1); }
  }
`;
